/**
 * DriveFilesView — read-rich files panel for the connector detail page.
 *
 * Replaces the old 3-column "card grid with X-to-remove" with a list
 * where each spreadsheet expands inline to show its tabs:
 *
 *   ▾ Sales-2024-25.xlsx        Excel · 3 of 7 sheets       [Filtered]  ✕
 *       ✓ Q1 Sales
 *       ✓ Q2 Sales
 *       ✓ Q3 Sales
 *       — Q4 Sales (not synced)
 *       — Refunds (not synced)
 *       …
 *
 * The "selected sheets" data comes from connector_metadata; the full sheet
 * list is fetched lazily from /drive-files/{file_id}/sheets the first time
 * a row is expanded so opening the page stays cheap.
 *
 * Editing is delegated to the canonical DriveFilesPickerModal: this page
 * exposes an "Edit selection" button that opens the same modal used on
 * /data-sync, so users get a single source of truth.
 */
import React from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Stack,
  Paper,
  Chip,
  Collapse,
  CircularProgress,
  Alert,
  Tooltip,
  Skeleton,
} from '@mui/material';
import CloseRounded from '@mui/icons-material/CloseRounded';
import EditIcon from '@mui/icons-material/Edit';
import InsertDriveFileRounded from '@mui/icons-material/InsertDriveFileRounded';
import FolderRounded from '@mui/icons-material/FolderRounded';
import GridOnRounded from '@mui/icons-material/GridOnRounded';
import ArticleRounded from '@mui/icons-material/ArticleRounded';
import ChevronRightRounded from '@mui/icons-material/ChevronRightRounded';
import ExpandMoreRounded from '@mui/icons-material/ExpandMoreRounded';
import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded';
import RemoveCircleOutlineRounded from '@mui/icons-material/RemoveCircleOutlineRounded';
import AccountTreeRounded from '@mui/icons-material/AccountTreeRounded';
import ArrowForwardRounded from '@mui/icons-material/ArrowForwardRounded';
import { buildApiUrl } from '../../config';
import type { DriveFile } from '../../components/DriveFilesPickerModal';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const SPREADSHEET_MIMES = new Set([
  'application/vnd.google-apps.spreadsheet',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
]);

function isSpreadsheet(mimeType?: string) {
  if (!mimeType) return false;
  if (SPREADSHEET_MIMES.has(mimeType)) return true;
  return /spreadsheet|excel/i.test(mimeType);
}

const FOLDER_MIME = 'application/vnd.google-apps.folder';

function isFolder(mimeType?: string) {
  return mimeType === FOLDER_MIME;
}

function fileIcon(mimeType?: string) {
  if (isFolder(mimeType)) return FolderRounded;
  if (isSpreadsheet(mimeType)) return GridOnRounded;
  if (mimeType?.includes('csv')) return ArticleRounded;
  return InsertDriveFileRounded;
}

function fileTypeLabel(mimeType?: string): string {
  if (!mimeType) return 'File';
  if (mimeType === FOLDER_MIME) return 'Folder';
  if (mimeType === 'application/vnd.google-apps.spreadsheet') return 'Google Sheet';
  if (mimeType.includes('spreadsheetml')) return 'Excel';
  if (mimeType.includes('ms-excel')) return 'Excel';
  if (mimeType.includes('csv')) return 'CSV';
  return 'File';
}

function fileIconBg(mimeType?: string): string {
  if (mimeType === FOLDER_MIME) return '#FBBC0418';
  if (mimeType === 'application/vnd.google-apps.spreadsheet') return '#0F9D5818';
  if (mimeType?.includes('csv')) return '#34A85318';
  if (isSpreadsheet(mimeType)) return '#1B6E4318';
  return 'rgba(0,0,0,0.04)';
}

function fileIconColor(mimeType?: string): string {
  if (mimeType === FOLDER_MIME) return '#F9AB00';
  if (mimeType === 'application/vnd.google-apps.spreadsheet') return '#0F9D58';
  if (mimeType?.includes('csv')) return '#34A853';
  if (isSpreadsheet(mimeType)) return '#1B6E43';
  return '#5F6368';
}

function authHeaders(): HeadersInit {
  const token = window.localStorage.getItem('klaris_jwt');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

interface SheetCacheEntry {
  loading: boolean;
  sheets: string[] | null;
  error: string | null;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export interface DriveFilesViewProps {
  tenantId: string;
  connectorId: string;
  files: DriveFile[];
  /** Called to remove a single file from the selection (PATCHes connector). */
  onRemove: (fileId: string) => void | Promise<void>;
  /** Opens the canonical DriveFilesPickerModal for full editing. */
  onEdit: () => void;
  /** When true, render skeleton rows while parent fetches detail. */
  loading?: boolean;
  /** Lookup of source_entity (filename) or source_entity_id (Drive file ID)
   *  → canonical entity name. When a file appears in this map, the row
   *  renders an entity-mapping chip so users can immediately see which
   *  canonical entity their file is feeding. */
  entityMap?: Record<string, string[]>;
  /** Called when the entity chip is clicked — navigates to the entity's
   *  detail page pre-filtered to the given source table/file. */
  onOpenEntity?: (entityName: string, sourceKey: string) => void;
}

const DriveFilesView: React.FC<DriveFilesViewProps> = ({
  tenantId,
  connectorId,
  files,
  onRemove,
  onEdit,
  loading,
  entityMap,
  onOpenEntity,
}) => {
  const [expanded, setExpanded] = React.useState<Set<string>>(new Set());
  const [sheetCache, setSheetCache] = React.useState<Record<string, SheetCacheEntry>>({});

  const loadSheets = React.useCallback(async (fileId: string) => {
    setSheetCache(prev => {
      if (prev[fileId]?.sheets || prev[fileId]?.loading) return prev;
      return { ...prev, [fileId]: { loading: true, sheets: null, error: null } };
    });
    try {
      const r = await fetch(
        buildApiUrl(`/api/v1/tenants/${tenantId}/connectors/${connectorId}/drive-files/${encodeURIComponent(fileId)}/sheets`),
        { headers: authHeaders() },
      );
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error((body as any).detail || `Failed to load sheets (${r.status})`);
      }
      const data = await r.json();
      const sheetNames: string[] = (data.sheets || []).map((s: any) => s.name);
      setSheetCache(prev => ({
        ...prev,
        [fileId]: { loading: false, sheets: sheetNames, error: null },
      }));
    } catch (e: any) {
      setSheetCache(prev => ({
        ...prev,
        [fileId]: { loading: false, sheets: null, error: e?.message || 'Failed to load sheets' },
      }));
    }
  }, [tenantId, connectorId]);

  const toggleExpanded = React.useCallback((fileId: string, mimeType?: string) => {
    if (!isSpreadsheet(mimeType)) return;
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(fileId)) {
        next.delete(fileId);
      } else {
        next.add(fileId);
        if (!sheetCache[fileId]?.sheets && !sheetCache[fileId]?.loading) {
          loadSheets(fileId);
        }
      }
      return next;
    });
  }, [sheetCache, loadSheets]);

  // -------- summary chips --------------------------------------------------
  const filteredFilesCount = files.reduce(
    (n, f) => n + (Array.isArray(f.selected_sheets) ? 1 : 0),
    0,
  );

  // -------- render ---------------------------------------------------------
  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {[1, 2, 3].map(i => (
          <Box
            key={i}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              px: 1.5,
              py: 1.25,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1.5,
            }}
          >
            <Skeleton variant="rounded" width={32} height={32} sx={{ borderRadius: 1, flexShrink: 0 }} />
            <Box sx={{ flex: 1 }}>
              <Skeleton variant="text" width="50%" height={18} />
              <Skeleton variant="text" width={120} height={14} />
            </Box>
          </Box>
        ))}
      </Box>
    );
  }

  if (files.length === 0) {
    return (
      <Paper
        variant="outlined"
        sx={{
          p: 4,
          textAlign: 'center',
          bgcolor: 'background.default',
          borderRadius: 1.5,
          borderStyle: 'dashed',
        }}
      >
        <InsertDriveFileRounded sx={{ fontSize: 44, color: 'text.disabled', mb: 1 }} />
        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
          No files selected yet
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
          Pick the Google Sheets, Excel, or CSV files you want Klaris to sync.
        </Typography>
        <Button
          variant="contained"
          size="small"
          disableElevation
          startIcon={<EditIcon sx={{ fontSize: 16 }} />}
          onClick={onEdit}
          sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600 }}
        >
          Select files
        </Button>
      </Paper>
    );
  }

  return (
    <Box>
      {/* Header strip: counts + edit button */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
        spacing={1}
        sx={{ mb: 1.25 }}
      >
        <Stack direction="row" gap={0.75} alignItems="center" flexWrap="wrap">
          <Chip
            label={`${files.length} file${files.length !== 1 ? 's' : ''} selected`}
            color="primary"
            size="small"
            variant="outlined"
            sx={{ fontWeight: 600 }}
          />
          {filteredFilesCount > 0 && (
            <Chip
              label={`${filteredFilesCount} with sheet filter${filteredFilesCount !== 1 ? 's' : ''}`}
              size="small"
              variant="outlined"
              sx={(t) => ({
                fontWeight: 600,
                color: 'text.secondary',
                borderColor: t.palette.divider,
              })}
            />
          )}
        </Stack>
        <Button
          size="small"
          variant="outlined"
          startIcon={<EditIcon sx={{ fontSize: 16 }} />}
          onClick={onEdit}
          sx={{
            borderRadius: '8px',
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.8125rem',
            px: 1.5,
            py: 0.5,
          }}
        >
          Edit selection
        </Button>
      </Stack>

      {/* List */}
      <Paper variant="outlined" sx={{ borderRadius: 1.5, overflow: 'hidden' }}>
        {files.map((f, idx) => {
          // Mappings can key on either filename or Drive file ID depending on
          // how the ontology builder recorded it — try both.
          const sourceKey = entityMap?.[f.name] ? f.name : f.id;
          const mappedEntities: string[] = entityMap?.[f.name] || entityMap?.[f.id] || [];
          return (
            <FileRow
              key={f.id}
              file={f}
              first={idx === 0}
              expanded={expanded.has(f.id)}
              sheetCache={sheetCache[f.id]}
              onToggleExpand={() => toggleExpanded(f.id, f.mimeType)}
              onRemove={() => onRemove(f.id)}
              mappedEntities={mappedEntities}
              onOpenEntity={onOpenEntity}
              sourceKey={sourceKey}
            />
          );
        })}
      </Paper>
    </Box>
  );
};

export default DriveFilesView;

// ---------------------------------------------------------------------------
// Row
// ---------------------------------------------------------------------------

interface FileRowProps {
  file: DriveFile;
  first: boolean;
  expanded: boolean;
  sheetCache?: SheetCacheEntry;
  onToggleExpand: () => void;
  onRemove: () => void;
  /** Canonical entities this file is mapped to (can be >1 if columns were split). */
  mappedEntities?: string[];
  /** sourceKey is the raw source_entity key stored in the canonical graph for this file. */
  onOpenEntity?: (entityName: string, sourceKey: string) => void;
  sourceKey?: string;
}

const FileRow: React.FC<FileRowProps> = ({
  file,
  first,
  expanded,
  sheetCache,
  onToggleExpand,
  onRemove,
  mappedEntities,
  onOpenEntity,
  sourceKey,
}) => {
  const Icon = fileIcon(file.mimeType);
  const expandable = isSpreadsheet(file.mimeType);
  const totalSheets = sheetCache?.sheets?.length;
  const selected = file.selected_sheets;
  const allSheets = selected == null;
  const explicitlyEmpty = Array.isArray(selected) && selected.length === 0;

  const summary = (() => {
    if (!expandable) return fileTypeLabel(file.mimeType);
    if (totalSheets == null) {
      // We don't yet know how many tabs exist. Fall back to whatever the
      // user picked (or "All sheets" by default).
      if (allSheets) return `${fileTypeLabel(file.mimeType)} · All sheets`;
      if (explicitlyEmpty) return `${fileTypeLabel(file.mimeType)} · No sheets`;
      return `${fileTypeLabel(file.mimeType)} · ${selected!.length} sheets`;
    }
    if (allSheets) return `${fileTypeLabel(file.mimeType)} · All sheets (${totalSheets})`;
    if (explicitlyEmpty) return `${fileTypeLabel(file.mimeType)} · No sheets selected`;
    return `${fileTypeLabel(file.mimeType)} · ${selected!.length} of ${totalSheets} sheets`;
  })();

  const showFilteredChip =
    expandable && Array.isArray(selected) && (totalSheets == null || selected.length !== totalSheets);

  return (
    <Box sx={(t) => ({ borderTop: first ? 'none' : `1px solid ${t.palette.divider}` })}>
      <Box
        onClick={expandable ? onToggleExpand : undefined}
        sx={(t) => ({
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 2,
          py: 1.25,
          cursor: expandable ? 'pointer' : 'default',
          transition: 'background-color 120ms ease',
          '&:hover': expandable ? { bgcolor: t.palette.action.hover } : undefined,
        })}
      >
        {expandable ? (
          <Box sx={{ width: 18, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.disabled' }}>
            {expanded ? <ExpandMoreRounded sx={{ fontSize: 18 }} /> : <ChevronRightRounded sx={{ fontSize: 18 }} />}
          </Box>
        ) : (
          <Box sx={{ width: 18 }} />
        )}
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: 1.5,
            bgcolor: fileIconBg(file.mimeType),
            color: fileIconColor(file.mimeType),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Icon sx={{ fontSize: 20 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.875rem', mb: 0.25 }} noWrap title={file.name}>
            {file.name}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
            {summary}
          </Typography>
        </Box>
        {mappedEntities && mappedEntities.length > 0 ? (
          <Stack direction="row" alignItems="center" spacing={0.4} sx={{ flexShrink: 0, flexWrap: 'wrap', justifyContent: 'flex-end', gap: 0.4 }}>
            <Typography variant="caption" sx={{ fontSize: '0.6875rem', color: 'text.disabled', whiteSpace: 'nowrap' }}>
              {mappedEntities.length > 1 ? 'Entities:' : 'Entity:'}
            </Typography>
            {mappedEntities.map((eName: string) => (
              <Tooltip key={eName} title="Open in Data Model filtered to this source">
                <Chip
                  size="small"
                  icon={<AccountTreeRounded sx={{ fontSize: '0.75rem !important' }} />}
                  label={eName}
                  deleteIcon={<ArrowForwardRounded sx={{ fontSize: '0.75rem !important' }} />}
                  variant="outlined"
                  color="primary"
                  clickable={!!onOpenEntity}
                  onClick={(e) => { e.stopPropagation(); if (onOpenEntity) onOpenEntity(eName, sourceKey || file.name); }}
                  onDelete={(e) => { e.stopPropagation(); if (onOpenEntity) onOpenEntity(eName, sourceKey || file.name); }}
                  sx={{ height: 22, fontSize: '0.6875rem', fontWeight: 600 }}
                />
              </Tooltip>
            ))}
          </Stack>
        ) : (
          <Tooltip title="Not yet mapped to a canonical entity. Run Build Data Model to map.">
            <Chip
              size="small"
              label="Not mapped"
              variant="outlined"
              sx={{ height: 22, fontSize: '0.6875rem', color: 'text.disabled', borderColor: 'divider', flexShrink: 0 }}
            />
          </Tooltip>
        )}
        {showFilteredChip && (
          <Chip
            size="small"
            label={explicitlyEmpty ? 'Empty' : 'Filtered'}
            color={explicitlyEmpty ? 'warning' : 'primary'}
            variant="outlined"
            sx={{
              height: 20,
              fontSize: '0.6875rem',
              fontWeight: 600,
              borderRadius: 1,
            }}
          />
        )}
        <Tooltip title="Remove from selection">
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onRemove();
            }}
            sx={{
              color: 'text.disabled',
              '&:hover': { color: 'error.main', bgcolor: 'action.hover' },
            }}
          >
            <CloseRounded sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      </Box>

      {expandable && (
        <Collapse in={expanded} unmountOnExit>
          <SheetList file={file} cache={sheetCache} />
        </Collapse>
      )}
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Sheet list (read-only, with selected/not-selected indicators)
// ---------------------------------------------------------------------------

const SheetList: React.FC<{ file: DriveFile; cache?: SheetCacheEntry }> = ({ file, cache }) => {
  if (cache?.loading || !cache) {
    return (
      <Box sx={(t) => ({ pl: 7.5, pr: 2, pb: 1.25, pt: 0.5, bgcolor: t.palette.action.hover })}>
        <Stack direction="row" alignItems="center" gap={1}>
          <CircularProgress size={12} />
          <Typography variant="caption" color="text.secondary">Loading sheets…</Typography>
        </Stack>
      </Box>
    );
  }
  if (cache.error) {
    return (
      <Box sx={(t) => ({ pl: 7.5, pr: 2, pb: 1.5, pt: 0.5, bgcolor: t.palette.action.hover })}>
        <Alert severity="warning" sx={{ borderRadius: '8px', fontSize: '0.8125rem' }}>{cache.error}</Alert>
      </Box>
    );
  }
  const sheets = cache.sheets || [];
  if (sheets.length === 0) {
    return (
      <Box sx={(t) => ({ pl: 7.5, pr: 2, pb: 1.5, pt: 0.5, bgcolor: t.palette.action.hover })}>
        <Typography variant="caption" color="text.secondary">No tabs found in this file.</Typography>
      </Box>
    );
  }

  const isAll = file.selected_sheets == null;
  const selectedSet = new Set(isAll ? sheets : (file.selected_sheets || []));
  const selectedCount = selectedSet.size;

  return (
    <Box sx={(t) => ({
      pl: { xs: 5.5, sm: 7.5 },
      pr: 2,
      py: 1,
      bgcolor: t.palette.action.hover,
      borderTop: `1px solid ${t.palette.divider}`,
    })}>
      <Typography
        variant="caption"
        fontWeight={600}
        color="text.secondary"
        sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.6875rem', display: 'block', mb: 0.75 }}
      >
        Tabs · {selectedCount} of {sheets.length} synced
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, gap: 0.25 }}>
        {sheets.map(name => {
          const checked = selectedSet.has(name);
          return (
            <Box
              key={name}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 0.75,
                py: 0.5,
                minWidth: 0,
              }}
            >
              {checked ? (
                <CheckCircleRounded sx={{ fontSize: 14, color: 'success.main', flexShrink: 0 }} />
              ) : (
                <RemoveCircleOutlineRounded sx={{ fontSize: 14, color: 'text.disabled', flexShrink: 0 }} />
              )}
              <Typography
                variant="body2"
                noWrap
                title={name}
                sx={{
                  fontSize: '0.8125rem',
                  fontWeight: checked ? 500 : 400,
                  color: checked ? 'text.primary' : 'text.disabled',
                }}
              >
                {name}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};
